import type { ConsolaReporter, LogObject } from "consola"
import { LogLevels, consola } from "consola"
import { formatInTimeZone } from "date-fns-tz"

/**
 * Default consola level: verbose in dev, errors + warnings only everywhere else.
 *
 * Level 1 drops `.start()`, `.success()`, `.info()`, `.log()` and `.debug()`, which is
 * where nearly all of our log volume comes from (~18 lifecycle lines per rendered page
 * plus one line per Kafka message). `.error()` and `.warn()` still get through — those
 * are the ones that carry operational signal (JET-2325).
 */
const DEFAULT_LOG_LEVEL = import.meta.dev ? LogLevels.info : LogLevels.warn

/**
 * Resolves `NUXT_LOG_LEVEL` into a consola level so the verbosity can be turned up
 * per environment (or temporarily during an incident) without a code change.
 *
 * Accepts either a number (`"0"`–`"5"`, see consola's LogLevels) or a consola type
 * name (`"error"`, `"warn"`, `"info"`, `"debug"`, ...). Anything unparsable falls
 * back to the default rather than silently muting or unmuting the service.
 */
function resolveLogLevel(raw: string | undefined): number {
  const value = raw?.trim()
  if (!value)
    return DEFAULT_LOG_LEVEL

  const numeric = Number(value)
  if (Number.isInteger(numeric))
    return numeric

  const named = LogLevels[value.toLowerCase() as keyof typeof LogLevels]
  return typeof named === "number" ? named : DEFAULT_LOG_LEVEL
}

// Set once, centrally: `Logger()` hands out tagged child instances that inherit this
// level, so no call site needs to know about it.
consola.level = resolveLogLevel(globalThis.process?.env?.NUXT_LOG_LEVEL)

function formatValue(value: unknown): string {
  if (typeof value === "string")
    return value
  if (value instanceof Error)
    return value.stack ?? `${value.name}: ${value.message}`

  try {
    return JSON.stringify(value) ?? String(value)
  }
  catch {
    // Circular references and the like — better a lossy line than a thrown logger.
    return String(value)
  }
}

/**
 * Renders a log event as exactly one non-empty line.
 *
 * Embedded newlines (stack traces, pretty-printed objects) are folded into the same
 * line, so one event stays one record instead of fragmenting into N of them.
 */
export function formatLogLine(logObj: LogObject): string {
  const tag = logObj.tag ? `[${logObj.tag}] ` : ""
  const message = (logObj.args ?? []).map(formatValue).join(" ")

  return `${tag}${logObj.type.toUpperCase()} ${message}`
    .replace(/\s*\r?\n\s*/g, " | ")
    .trim()
}

/**
 * consola's default (fancy) reporter wraps every log whose level is < 2 in blank
 * lines: `return isBadge ? "\n" + line + "\n" : line`. Those are exactly the levels
 * we keep in production — warn and error — so each one emits two empty stdout lines
 * alongside the real one.
 *
 * FireLens turns every stdout *line* into one record shaped `{ "log": "<line>" }`, and
 * the ECS task ships them to VictoriaLogs with `_msg_field=log`. An empty line becomes
 * an empty `_msg`, which VictoriaLogs rejects with "missing _msg field".
 *
 * So outside of dev we render single-line output ourselves. Dev keeps the pretty
 * reporter — nothing is scraping a developer's terminal.
 */
export const singleLineReporter: ConsolaReporter = {
  log(logObj) {
    const line = formatLogLine(logObj)
    if (!line)
      return

    const stream = logObj.level < 2 ? globalThis.process?.stderr : globalThis.process?.stdout
    if (!stream) {
      console.error(line)
      return
    }

    stream.write(`${line}\n`)
  },
}

// `!import.meta.client` keeps this reporter out of the browser bundle: it renders for
// FireLens (one stdout line per event, stack traces folded onto that line), which is
// wrong for a devtools console and, before the `process` guard above, crashed the app
// form outright. Negated rather than `import.meta.server` so the Nitro-hosted Kafka
// handlers keep single-line output too.
//
// NODE_ENV (rather than import.meta.dev) so this stays off in unit tests, where the
// default reporter keeps vitest's console capture intact. The Dockerfile pins
// NODE_ENV=production for every deployed environment.
if (!import.meta.client && globalThis.process?.env?.NODE_ENV === "production")
  consola.setReporters([singleLineReporter])

// Match format of logged time by our Java services
function getBerlinTime() {
  const now = new Date()
  const timeZone = "Europe/Berlin"
  const pattern = "yyyy-MM-dd HH:mm:ss.SSS"
  return formatInTimeZone(now, timeZone, pattern)
}

/**
 * Returns a consolas instance and can be used to log to the console
 * Supports all consola methods and message types (e.g. .error(), .success(), .start(), etc.)
 * @param requestId optional requestId to be included
 * @returns ConsolaInstance
 */
export default function Logger(requestId?: string) {
  const dateTime = getBerlinTime()

  const tag = requestId ? `${dateTime} | ${requestId}` : `${dateTime}`
  return consola.withTag(tag)
}
